import React, { useState, useEffect } from 'react';
import * as mui from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { SectionHeader } from './UIComponents';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import NetworkService from '../../../../services/NetworkService';
import SecurityIcon from '@mui/icons-material/Security';
import PersonIcon from '@mui/icons-material/Person';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpIcon from '@mui/icons-material/Help';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SearchIcon from '@mui/icons-material/Search';

// Helper to detect likely service accounts
const isServiceAccount = (username) => {
  if (!username) return false;
  
  const serviceAccountPatterns = [
    /^svc/i,        // Starts with svc
    /^service/i,     // Starts with service
    /^admin/i,       // Starts with admin
    /^sa_/i,         // Starts with sa_
    /^app_/i,        // Starts with app_
    /^sys/i,         // Starts with sys
    /^srv/i,         // Starts with srv
    /\$$/,           // Ends with $ (common for machine accounts)
    /^[^@]*\d{3,}/   // Contains 3 or more consecutive digits
  ];
  
  return serviceAccountPatterns.some(pattern => pattern.test(username));
};

const NetworkAdmins = ({ domains, showNotification }) => {
  const [adminGroups, setAdminGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});
  const [filterText, setFilterText] = useState('');
  const [hrStatusFilter, setHrStatusFilter] = useState('all');
  
  useEffect(() => {
    // Set the default domain if available
    if (domains.length > 0 && !selectedDomain) {
      setSelectedDomain(domains[0].id);
    }
  }, [domains, selectedDomain]);
  
  useEffect(() => {
    // Fetch data when domain changes
    if (selectedDomain) {
      fetchNetworkAdmins(selectedDomain);
    }
  }, [selectedDomain]);
  
  const fetchNetworkAdmins = async (domainId) => {
    setLoading(true);
    try {
      const data = await NetworkService.getSoxAdmins(domainId);
      setAdminGroups(data);
    } catch (error) {
      console.error('Error fetching network admins:', error);
      showNotification('Error', error.message || 'Failed to fetch network admins');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDomainChange = (event) => {
    setSelectedDomain(event.target.value);
  };
  
  const toggleGroupExpansion = (groupName) => {
    setExpandedGroups({
      ...expandedGroups,
      [groupName]: !expandedGroups[groupName]
    });
  };
  
  const handleRefresh = () => {
    if (selectedDomain) {
      fetchNetworkAdmins(selectedDomain);
    }
  };

  const handleHrStatusFilterChange = (event) => {
    setHrStatusFilter(event.target.value);
  };
  
  // Calculate stats for all admin users
  const calculateStats = () => {
    let totalAdmins = 0;
    let needsReviewAdmins = 0;
    let notInHrAdmins = 0;
    let inactiveInHrAdmins = 0;
    
    adminGroups.forEach(group => {
      group.members.forEach(member => {
        totalAdmins++;
        
        // Skip service accounts when counting "needs review" admins
        if (!isServiceAccount(member.username)) {
          if (member.risk_level === 'High') needsReviewAdmins++;
          if (!member.hr_found) notInHrAdmins++;
          if (member.hr_found && (member.hr_status === 'Inactive' || member.hr_status === 'Terminated')) {
            inactiveInHrAdmins++;
          }
        }
      });
    });
    
    return {
      totalAdmins,
      needsReviewAdmins,
      notInHrAdmins,
      inactiveInHrAdmins
    };
  };
  
  const stats = calculateStats();
  
  // Filter groups and members based on filter text and HR status
  const filteredGroups = adminGroups.map(group => {
    // Filter members based on criteria
    const filteredMembers = group.members.filter(member => {
      // Text filter
      const matchesText = 
        member.username.toLowerCase().includes(filterText.toLowerCase()) ||
        member.display_name.toLowerCase().includes(filterText.toLowerCase()) ||
        (member.email && member.email.toLowerCase().includes(filterText.toLowerCase()));
        
      // HR status filter
      let matchesHrStatus = true;
      if (hrStatusFilter === 'not-in-hr') {
        matchesHrStatus = !member.hr_found && !isServiceAccount(member.username);
      } else if (hrStatusFilter === 'inactive-in-hr') {
        matchesHrStatus = member.hr_found && 
          (member.hr_status === 'Inactive' || member.hr_status === 'Terminated');
      } else if (hrStatusFilter === 'active-in-hr') {
        matchesHrStatus = member.hr_found && member.hr_status === 'Active';
      } else if (hrStatusFilter === 'needs-review') {
        matchesHrStatus = member.risk_level === 'High' && !isServiceAccount(member.username);
      } else if (hrStatusFilter === 'service-accounts') {
        matchesHrStatus = isServiceAccount(member.username);
      }
      
      return matchesText && matchesHrStatus;
    });
    
    // Only include groups that have matching members or match the group name filter
    return {
      ...group,
      members: filteredMembers,
      hasMatches: filteredMembers.length > 0 || 
                 group.group_name.toLowerCase().includes(filterText.toLowerCase())
    };
  }).filter(group => group.hasMatches);
  
  // Define columns for the member DataGrid
  const memberColumns = [
    { 
      field: 'username', 
      headerName: 'Username', 
      flex: 1,
      renderCell: (params) => {
        if (!params || !params.row) {
          return <span>Error: Missing Data</span>;
        }
        
        // Check if it's a service account
        const isService = isServiceAccount(params.value);
        
        // Create tooltip content with matching information
        let tooltipTitle = "Unknown status";
        if (isService) {
          tooltipTitle = "Service account (automatically excluded from HR verification)";
        } else if (params.row.hr_found) {
          tooltipTitle = `Matched with HR via ${params.row.match_method || 'unknown method'}`;
        } else {
          tooltipTitle = 'No match found in HR records';
        }

        return (
          <mui.Tooltip title={tooltipTitle}>
            <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonIcon fontSize="small" sx={{ 
                mr: 1, 
                color: isService
                  ? 'primary.light'
                  : (params.row.risk_level === 'High' ? 'text.secondary' : 'text.secondary') 
              }} />
              <mui.Typography 
                sx={{ 
                  color: isService 
                    ? 'primary.main'
                    : (params.row.risk_level === 'High' ? 'text.primary' : 'inherit'),
                  fontWeight: isService ? 'medium' : 'regular',
                  fontStyle: isService ? 'italic' : 'normal'
                }}
              >
                {params.value || 'Unknown'}
                {isService && (
                  <mui.Box component="span" sx={{ ml: 1, opacity: 0.7, fontSize: '0.8rem' }}>
                    (service)
                  </mui.Box>
                )}
              </mui.Typography>
            </mui.Box>
          </mui.Tooltip>
        );
      }
    },
    { field: 'display_name', headerName: 'Display Name', flex: 1 },
    { field: 'email', headerName: 'Email', flex: 1.5 },
    { 
      field: 'status', 
      headerName: 'AD Status', 
      width: 110,
      renderCell: (params) => {
        if (!params || params.value === null || params.value === undefined) {
          return <mui.Chip label="Unknown" size="small" color="default" />;
        }
        
        const isActive = params.value && params.value.toLowerCase() === 'active';
        
        return (
          <mui.Chip 
            label={params.value} 
            size="small"
            color={isActive ? 'success' : 'default'}
            variant={isActive ? 'filled' : 'outlined'}
          />
        );
      }
    },
    {
      field: 'hr_status',
      headerName: 'HR Status',
      width: 140,
      renderCell: (params) => {
        if (!params || !params.row) {
          return <mui.Chip label="Error" size="small" color="default" />;
        }
        
        if (!params.row.hr_found) {
          return (
            <mui.Tooltip title="User not found in HR records">
              <mui.Chip 
                icon={<ErrorIcon />}
                label="Not Found" 
                size="small"
                color="error"
                variant="outlined"
              />
            </mui.Tooltip>
          );
        }
        
        if (!params.row.hr_status) {
          return (
            <mui.Chip 
              icon={<HelpIcon />}
              label="Unknown" 
              size="small"
              color="info"
              variant="outlined"
            />
          );
        }
        
        const isActive = params.row.hr_status && params.row.hr_status.toLowerCase() === 'active';
        const isTerminated = params.row.hr_status && params.row.hr_status.toLowerCase() === 'terminated';
        
        return (
          <mui.Chip 
            label={params.row.hr_status} 
            size="small"
            color={isActive ? 'success' : (isTerminated ? 'error' : 'warning')}
            variant={isActive ? 'filled' : 'outlined'}
          />
        );
      }
    },
    { 
      field: 'hr_termination_date', 
      headerName: 'HR Term Date', 
      width: 150,
      valueFormatter: (params) => {
        if (!params || params.value === null || params.value === undefined) return '';
        try {
          return new Date(params.value).toLocaleDateString();
        } catch (e) {
          return String(params.value || '');
        }
      },
      renderCell: (params) => {
        if (!params || !params.value) return null;
        
        // Don't show termination date if HR status is active
        if (params.row.hr_status && params.row.hr_status.toLowerCase() === 'active') {
          return null;
        }
        
        try {
          return (
            <mui.Typography variant="body2" color="text.secondary">
              {new Date(params.value).toLocaleDateString()}
            </mui.Typography>
          );
        } catch (e) {
          return <mui.Typography variant="body2">{String(params.value || '')}</mui.Typography>;
        }
      }
    },
    { 
      field: 'risk_level', 
      headerName: 'Risk', 
      width: 100,
      renderCell: (params) => {
        // For service accounts, display a different indicator
        if (isServiceAccount(params.row?.username)) {
          return (
            <mui.Chip 
              label="Service Acct" 
              size="small"
              color="primary"
              variant="outlined"
            />
          );
        }
        
        // For regular accounts
        const isHighRisk = params.value === 'High';
        
        return (
          <mui.Chip 
            label={isHighRisk ? 'Needs Review' : (params.value || 'Unknown')} 
            size="small"
            color={isHighRisk ? 'warning' : 'default'}
            variant={isHighRisk ? 'filled' : 'outlined'}
          />
        );
      }
    },
    { 
      field: 'created_date', 
      headerName: 'Created', 
      width: 150,
      valueFormatter: (params) => {
        if (!params || params.value === null || params.value === undefined) return 'Unknown';
        try {
          return new Date(params.value).toLocaleDateString();
        } catch (e) {
          return String(params.value || 'Unknown');
        }
      }
    },
    { 
      field: 'modified_date', 
      headerName: 'Last Modified', 
      width: 150,
      valueFormatter: (params) => {
        if (!params || params.value === null || params.value === undefined) return 'Unknown';
        try {
          return new Date(params.value).toLocaleDateString();
        } catch (e) {
          return String(params.value || 'Unknown');
        }
      }
    },
  ];
  
  // Get severity level color for admin groups
  const getGroupSeverity = (groupName) => {
    const highRiskGroups = ['Domain Admins', 'Enterprise Admins', 'Schema Admins', 'Administrators'];
    const mediumRiskGroups = ['Server Operators', 'Backup Operators', 'Account Operators'];
    
    if (highRiskGroups.includes(groupName)) {
      return 'error';
    } else if (mediumRiskGroups.includes(groupName)) {
      return 'warning';
    }
    return 'info';
  };
  
  return (
    <mui.Paper sx={{ p: 3, mb: 4 }}>
      <SectionHeader 
        title="Network Admins" 
        icon={<AdminPanelSettingsIcon />} 
        description="Domain administrators and privileged user accounts for compliance" 
        action={
          <mui.Stack direction="row" spacing={2}>
            <mui.FormControl sx={{ minWidth: 200 }}>
              <mui.InputLabel id="domain-select-label">Domain</mui.InputLabel>
              <mui.Select
                labelId="domain-select-label"
                value={selectedDomain}
                label="Domain"
                onChange={handleDomainChange}
                size="small"
              >
                {domains.map((domain) => (
                  <mui.MenuItem key={domain.id} value={domain.id}>
                    {domain.DOMAIN_NAME}
                  </mui.MenuItem>
                ))}
              </mui.Select>
            </mui.FormControl>
            <mui.Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={loading}
            >
              Refresh
            </mui.Button>
          </mui.Stack>
        }
      />
      
      {/* Stats section */}
      {adminGroups.length > 0 && (
        <mui.Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          <mui.Grid container spacing={2}>
            <mui.Grid item xs={12} sm={6} md={3}>
              <mui.Box sx={{ p: 1, textAlign: 'center' }}>
                <mui.Typography variant="body2" color="text.secondary">Total Admins</mui.Typography>
                <mui.Typography variant="h4" color="primary.main" fontWeight="bold">{stats.totalAdmins}</mui.Typography>
              </mui.Box>
            </mui.Grid>
            <mui.Grid item xs={12} sm={6} md={3}>
              <mui.Box sx={{ p: 1, textAlign: 'center' }}>
                <mui.Typography variant="body2" color="text.secondary">Needs Review</mui.Typography>
                <mui.Typography variant="h4" color="text.primary" fontWeight="bold">{stats.needsReviewAdmins}</mui.Typography>
              </mui.Box>
            </mui.Grid>
            <mui.Grid item xs={12} sm={6} md={3}>
              <mui.Box sx={{ p: 1, textAlign: 'center' }}>
                <mui.Typography variant="body2" color="text.secondary">Not in HR</mui.Typography>
                <mui.Typography variant="h4" color="text.primary" fontWeight="bold">{stats.notInHrAdmins}</mui.Typography>
              </mui.Box>
            </mui.Grid>
            <mui.Grid item xs={12} sm={6} md={3}>
              <mui.Box sx={{ p: 1, textAlign: 'center' }}>
                <mui.Typography variant="body2" color="text.secondary">Inactive in HR</mui.Typography>
                <mui.Typography variant="h4" color="text.primary" fontWeight="bold">{stats.inactiveInHrAdmins}</mui.Typography>
              </mui.Box>
            </mui.Grid>
          </mui.Grid>
        </mui.Paper>
      )}
      
      {/* Filter controls */}
      <mui.Grid container spacing={2} sx={{ mb: 3 }}>
        <mui.Grid item xs={12} md={8}>
          <mui.TextField
            label="Filter Users or Groups"
            variant="outlined"
            size="small"
            fullWidth
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            InputProps={{
              startAdornment: (
                <mui.InputAdornment position="start">
                  <SearchIcon />
                </mui.InputAdornment>
              ),
            }}
          />
        </mui.Grid>
        <mui.Grid item xs={12} md={4}>
          <mui.FormControl fullWidth size="small">
            <mui.InputLabel>HR Status Filter</mui.InputLabel>
            <mui.Select
              value={hrStatusFilter}
              label="HR Status Filter"
              onChange={handleHrStatusFilterChange}
            >
              <mui.MenuItem value="all">Show All</mui.MenuItem>
              <mui.MenuItem value="needs-review">Needs Review Only</mui.MenuItem>
              <mui.MenuItem value="not-in-hr">Not in HR</mui.MenuItem>
              <mui.MenuItem value="inactive-in-hr">Inactive in HR</mui.MenuItem>
              <mui.MenuItem value="active-in-hr">Active in HR</mui.MenuItem>
              <mui.MenuItem value="service-accounts">Service Accounts</mui.MenuItem>
            </mui.Select>
          </mui.FormControl>
        </mui.Grid>
      </mui.Grid>
      
      {/* Alert for accounts to review */}
      {stats.needsReviewAdmins > 0 && (
        <mui.Alert severity="info" sx={{ mb: 3 }}>
          <mui.AlertTitle>Accounts For Review</mui.AlertTitle>
          {stats.needsReviewAdmins} admin account(s) may need verification. These include accounts not found in HR records or marked as inactive.
        </mui.Alert>
      )}
      
      {/* Main content */}
      {loading ? (
        <mui.Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <mui.CircularProgress />
        </mui.Box>
      ) : adminGroups.length === 0 ? (
        <mui.Alert severity="info" sx={{ mb: 2 }}>
          <mui.AlertTitle>No Admin Groups Found</mui.AlertTitle>
          No relevant admin groups were found in the selected domain.
        </mui.Alert>
      ) : filteredGroups.length === 0 ? (
        <mui.Alert severity="info" sx={{ mb: 2 }}>
          <mui.AlertTitle>No Matching Results</mui.AlertTitle>
          No admin users match your current filter criteria.
        </mui.Alert>
      ) : (
        <mui.Box>
          {filteredGroups.map((group) => (
            <mui.Card 
              key={group.group_name} 
              sx={{ 
                mb: 2,
                border: group.members.some(m => m.risk_level === 'High' && !isServiceAccount(m.username)) ? '1px solid' : 'none',
                borderColor: 'divider'
              }}
            >
              <mui.CardHeader
                avatar={
                  <mui.Avatar sx={{ bgcolor: `${getGroupSeverity(group.group_name)}.main` }}>
                    <SecurityIcon />
                  </mui.Avatar>
                }
                title={
                  <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <mui.Typography variant="h6">
                      {group.group_name}
                    </mui.Typography>
                    <mui.Chip 
                      label={`${group.members.length} members`}
                      size="small"
                      sx={{ ml: 2 }}
                      variant="outlined"
                    />
                    {group.members.some(m => m.risk_level === 'High' && !isServiceAccount(m.username)) && (
                      <mui.Chip 
                        label="Needs Review"
                        size="small"
                        color="warning"
                        icon={<WarningIcon fontSize="small" />}
                        sx={{ ml: 2 }}
                        variant="filled"
                      />
                    )}
                  </mui.Box>
                }
                subheader={group.description}
                action={
                  <mui.IconButton
                    onClick={() => toggleGroupExpansion(group.group_name)}
                    aria-expanded={expandedGroups[group.group_name]}
                    aria-label="show more"
                  >
                    <mui.IconButton 
                      color={getGroupSeverity(group.group_name)}
                      size="small"
                    >
                      <SupervisorAccountIcon />
                    </mui.IconButton>
                    {expandedGroups[group.group_name] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </mui.IconButton>
                }
              />
              
              <mui.Collapse in={expandedGroups[group.group_name]} timeout="auto" unmountOnExit>
                <mui.CardContent>
                  {group.members.length > 0 ? (
                    <mui.Box sx={{ height: 'auto', width: '100%' }}>
                      <DataGrid
                        rows={group.members.map((member, index) => ({
                          // Include default values for all fields to avoid undefined/null issues
                          username: "Unknown",
                          display_name: "Unknown",
                          email: "",
                          status: "Unknown",
                          risk_level: "Unknown",
                          created_date: null,
                          modified_date: null,
                          hr_found: false,
                          hr_status: null,
                          hr_termination_date: null,
                          // Now spread the actual values over the defaults
                          ...member,
                          // Ensure each row has an ID
                          id: index 
                        }))}
                        columns={memberColumns}
                        pageSize={5}
                        disableSelectionOnClick
                        autoHeight
                        getRowClassName={(params) => {
                          if (isServiceAccount(params.row.username)) {
                            return 'service-account-row';
                          }
                          return params.row.risk_level === 'High' ? 'needs-review-row' : '';
                        }}
                        sx={{
                          '& .needs-review-row': {
                            bgcolor: 'rgba(0, 0, 0, 0.04)',
                            '&:hover': {
                              bgcolor: 'rgba(0, 0, 0, 0.07)',
                            },
                          },
                          '& .service-account-row': {
                            bgcolor: 'rgba(25, 118, 210, 0.05)',
                            '&:hover': {
                              bgcolor: 'rgba(25, 118, 210, 0.08)',
                            },
                          },
                        }}
                        components={{
                          NoRowsOverlay: () => (
                            <mui.Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                                p: 2,
                              }}
                            >
                              <mui.Typography color="text.secondary">
                                No members to display
                              </mui.Typography>
                            </mui.Box>
                          ),
                          ErrorOverlay: (props) => (
                            <mui.Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                                p: 2,
                              }}
                            >
                              <ErrorIcon color="error" sx={{ mb: 1 }} />
                              <mui.Typography color="error">
                                Error loading member data
                              </mui.Typography>
                            </mui.Box>
                          )
                        }}
                      />
                    </mui.Box>
                  ) : (
                    <mui.Alert severity="warning" icon={<ErrorIcon />}>
                      This group has no members matching your filter criteria.
                    </mui.Alert>
                  )}
                </mui.CardContent>
              </mui.Collapse>
            </mui.Card>
          ))}
        </mui.Box>
      )}
    </mui.Paper>
  );
};

export default NetworkAdmins; 